import { useMutation } from '@tanstack/react-query';
import { ConfirmModal, Page } from 'corso-admin-ui';
import { PropsWithChildren, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '~/api';
import { useNavigateSearchData } from '~/components/searchTable/useNavigateSearchData';
import { useInvalidateProduct } from '~/features/products/ProductForm';
import { useProduct } from '~/features/products/ProductProvider';
import useIsAdmin from '~/hooks/useIsAdmin';
import { useToast } from '~/hooks/useToast';
import { useIsWidgetAuth } from '~/providers/AutoAuthProvider';
import { useStoreId } from '~/providers/StoreUserProvider';
import { isAnalyzableTitle, productAnalyticsDate } from './ProductAnalytics';

export function ProductPage({ children }: PropsWithChildren) {
  const product = useProduct();
  const storeId = useStoreId();
  const isWidgetAuth = useIsWidgetAuth();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const invalidateProduct = useInvalidateProduct();
  const toast = useToast();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const paginationActions = useNavigateSearchData({
    currentId: product.id,
    kind: 'Product',
  });

  const { mutate: deleteProduct, isPending: isDeletePending } = useMutation({
    mutationFn: () => api.store(storeId).product.delete(`${product.id}`),
    onSuccess: () => {
      toast.show('Product deleted');
      navigate(`/${storeId}/products`);
      return invalidateProduct();
    },
  });

  return (
    <Page
      backAction={!isWidgetAuth}
      paginationActions={paginationActions}
      title={
        product.platformAdminUrl
          ? {
              content: product.title,
              to: product.platformAdminUrl,
              target: '_blank',
            }
          : product.title
      }
      subtitle={<p>{product.isOffPlatform ? 'Corso' : 'Shopify'}</p>}
      secondaryActions={[
        {
          id: 'open-in-dashboards',
          content: 'Open in Dashboards',
          icon: 'analytics',
          hidden: !isAdmin || !isAnalyzableTitle(product.title),
          to: {
            pathname: `/${storeId}/analytics/dashboards`,
            search: new URLSearchParams([
              ['range', productAnalyticsDate.preset],
              ['productTitles', product.title],
            ]).toString(),
          },
        },
        {
          id: 'delete-product',
          content: 'Delete Product',
          hidden: !product.isOffPlatform,
          onAction: () => setShowDeleteConfirmation(true),
        },
      ]}
    >
      {children}
      <ConfirmModal
        onCancel={() => setShowDeleteConfirmation(false)}
        title="Delete Product"
        prompt={`Are you sure you want to delete ${product.title}? It will no longer be offered, but claim and registration history will be preserved.`}
        show={showDeleteConfirmation}
        loading={isDeletePending}
        confirmText="Delete"
        onConfirm={() => deleteProduct()}
      />
    </Page>
  );
}
